import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { DropdownMenu } from '@3plearning/react-components';
import { useLocalisation } from '@3plearning/lingui-cdn';

import SvgIcon from '@components/svg-icon/svg-icon';

import { mapEntitiesToOptions } from '@containers/entities/helpers';
import { setSelectedCourseOption, setSelectedPublication } from '@containers/upf-assign/upf-assign-actions';

import useExplicitServiceRequest from '@helpers/hooks/use-explicit-service-request';
import useManageLocalStorage from '@helpers/hooks/use-manage-local-storage';
import { LOCAL_STORAGE_KEYS } from '@helpers/constants';
import { PdfIcon } from '@helpers/icon-dictionary';
import { findOption } from '@helpers/utils';
import { useTracking } from '../../analytics';
import useGetDefaultUpfCourseId from '../../helpers/hooks/use-get-default-upf-course-id';
import useGetSelectedPublication from './hooks/use-get-selected-publication';
import useGetDefaultCourse from './hooks/use-get-default-course';
import useGetSortedCourse from './hooks/use-get-sorted-course';
import getTranslations from './translations';

import { Root, FilterList, AlignmentPdfText, AlignmentPdfWrapper, TopFiltersWrapper } from './styled';

const CourseFilters = () => {
  const [isCurriculumOrCourseChanged, setIsCurriculumOrCourseChanged] = useState(false);
  const selectedClass = useSelector(state => state.UPFAssignReducer.selectedClass, shallowEqual);
  const selectedGroup = useSelector(state => state.UPFAssignReducer.selectedGroup, shallowEqual);
  /**
   * Store
   */
  const dispatch = useDispatch();
  const publications = useSelector(state => state.entitiesReducer.publications);

  const courses = useSelector(state => state.entitiesReducer.courses);
  const selectedCourseOption = useSelector(state => state.UPFAssignReducer.selectedCourseOption, shallowEqual);
  const defaultUpfCourseId = useGetDefaultUpfCourseId();
  const { selectedPublication } = useGetSelectedPublication();
  /**
   * Local variables
   */
  const { courses: courseIds = [] } = publications[selectedPublication?.value] || {};
  const { isDocumentFileAvailable, masterCourseId } = courses[selectedCourseOption?.value] || {};
  const publicationCourses = courseIds.map(courseId => courses[courseId]);
  const publicationOptions = mapEntitiesToOptions(publications);
  const courseOptions = useGetSortedCourse(selectedPublication, publicationCourses);
  const { getExplicitRequestUrl } = useExplicitServiceRequest();
  const { trackCourseFilterChanged } = useTracking();

  // Derives the publication that contains the classroom's defaultUpfCourseId from the
  // BE response. Passed as defaultValue to useManageLocalStorage so it is the single
  // source of truth. Use stored value if valid, fall back to this.
  const defaultPublication = useMemo(() => {
    for (const key in publications) {
      if (publications[key].courses.includes(defaultUpfCourseId)) {
        return { value: publications[key].id, label: publications[key].name };
      }
    }
    return null;
  }, [publications, defaultUpfCourseId]);

  // Resolves the course matching the classroom's defaultUpfCourseId from the BE response.
  const defaultCourseOption = useGetDefaultCourse({
    courseOptions,
    defaultUpfCourseId,
    selectedClass,
    selectedGroup,
    isCurriculumOrCourseChanged
  });

  /**
   * Functions
   */
  const onPublicationChange = selectedOption => {
    setIsCurriculumOrCourseChanged(true);
    const { courses: selectedPublicationCourseIds } = publications[selectedOption.value];
    const firstCourseId = selectedPublicationCourseIds[0];
    const updatedSelectedCourse = courses[firstCourseId] || {};
    dispatch(setSelectedPublication(selectedOption));
    dispatch(setSelectedCourseOption({ label: updatedSelectedCourse.courseName, value: updatedSelectedCourse.id }));
    trackCourseFilterChanged({
      publicationId: selectedOption?.value,
      courseId: updatedSelectedCourse.id,
      source: 'publication'
    });
  };

  const onPublicationDownload = () => {
    const urlPath = `master-courses/${masterCourseId}/file`;
    const explicitUrl = getExplicitRequestUrl({ urlPath });
    if (explicitUrl) {
      window.location.assign(explicitUrl);
    }
  };

  useEffect(() => {
    setIsCurriculumOrCourseChanged(false);
  }, [selectedClass])

  const onCourseChange = selectedOption => {
    setIsCurriculumOrCourseChanged(true);
    dispatch(setSelectedCourseOption(selectedOption));
    trackCourseFilterChanged({ courseId: selectedOption?.value, source: 'course' });
  };

  /**
   * Hooks
   */
  const { i18n } = useLocalisation();

  useManageLocalStorage({
    checkShouldUpdateValueFromLocalStorage: localStorageValue => findOption(publicationOptions, localStorageValue),
    defaultValue: defaultPublication || publicationOptions[0],
    enable: !!publicationOptions[0],
    key: LOCAL_STORAGE_KEYS.LAST_USED_PUBLICATION,
    updateValueFromLocalStorage: updatedValue => dispatch(setSelectedPublication(updatedValue)),
    value: selectedPublication || publicationOptions[0]
  });

  useManageLocalStorage({
    checkShouldUpdateValueFromLocalStorage: localStorageValue => findOption(courseOptions, localStorageValue),
    defaultValue: defaultCourseOption || courseOptions[0],
    enable: !!(courseOptions[0] && selectedPublication?.value && selectedClass?.value && selectedGroup?.value),
    key: LOCAL_STORAGE_KEYS.LAST_USED_COURSE,
    updateValueFromLocalStorage: updatedValue => dispatch(setSelectedCourseOption(updatedValue)),
    value: selectedCourseOption || courseOptions[0]
  });

  // When the user switches curriculum, dispatch the course matching the defaultUpfCourseId
  // for the new curriculum. On page refresh this effect is skipped (isCurriculumOrCourseChanged
  // is false), leaving useManageLocalStorage as the source of truth for restoring state.
  useEffect(() => {
    if (isCurriculumOrCourseChanged && defaultCourseOption?.value) {
      dispatch(setSelectedCourseOption(defaultCourseOption));
    }
  }, [defaultCourseOption?.value]);

  /**
   * Translations
   */
  const { CURRICULUM_TEXT, COURSE_TEXT, PDF_TEXT } = getTranslations({ i18n });
  return (
    <Root>
      <TopFiltersWrapper>
        <FilterList>
          <label htmlFor="publications">{CURRICULUM_TEXT}</label>
          <DropdownMenu
            className="dropdown"
            data-name="publications"
            key="publications"
            onSelectOption={onPublicationChange}
            options={publicationOptions}
            selectedOption={selectedPublication}
          />
        </FilterList>

        <FilterList>
          <label htmlFor="courses">{COURSE_TEXT}</label>
          <DropdownMenu
            className="dropdown"
            data-name="courses"
            disabled={!selectedPublication?.value}
            key="courses"
            onSelectOption={onCourseChange}
            options={courseOptions}
            selectedOption={selectedCourseOption}
          />
        </FilterList>
      </TopFiltersWrapper>
      {isDocumentFileAvailable && (
        <AlignmentPdfWrapper onClick={() => onPublicationDownload()}>
          <SvgIcon icon={PdfIcon} />
          <AlignmentPdfText>{PDF_TEXT}</AlignmentPdfText>
        </AlignmentPdfWrapper>
      )}
    </Root>
  );
};

export default CourseFilters;
